// Leaf module (no imports): the video_click position/list basis shared by the
// click trackers and the video_preview state machine. Kept separate from
// base_video_click_tracker.ts so the eagerly-loaded shared `videoList`
// component does not pull the whole tracker class (and recommendations.ts)
// into every page's chunk just to reach these few lines.

export type VideoClickList = 'main' | 'related_right' | 'related_bottom' | 'playlist';

export interface VideoItemPositionSource {
    _getVideoItemPosition(videoItem: HTMLElement): number;
}

// Every initialised tracker registers itself here so other list features
// (video_preview) can reuse video_click's position basis without knowing
// which tracker owns the element.
const activeTrackers: VideoItemPositionSource[] = [];

/** Registers a tracker once; repeated init() on the same instance is a no-op. */
export const registerTracker = (tracker: VideoItemPositionSource): void => {
    if (!activeTrackers.includes(tracker)) {
        activeTrackers.push(tracker);
    }
};

/**
 * Parses a video item's `data-id` into a non-negative numeric id, or null when
 * the attribute is missing, empty or not a number. Number(null) and Number('')
 * are both 0, so the presence check must come before the conversion.
 */
export const parseVideoItemId = (videoItem: HTMLElement): number | null => {
    const rawId = videoItem.getAttribute('data-id');
    if (rawId === null || rawId === '') {
        return null;
    }

    const itemId = Number(rawId);
    if (Number.isNaN(itemId) || itemId < 0) {
        return null;
    }

    return itemId;
};

// Derived from the item's ancestor, not $root: the video page renders both
// related lists under one $root, so only the ancestor can disambiguate them.
export const getVideoClickList = (videoItem: HTMLElement): VideoClickList => {
    if (videoItem.closest('.js-related-videos-right')) {
        return 'related_right';
    }

    if (videoItem.closest('.js-related-videos-bottom')) {
        return 'related_bottom';
    }

    // The id-based #playlist-list is the video-page playlist (video_playlist.html).
    // The separate class-based .playlist-list surfaces live on pages this tracker
    // does not cover, so they correctly fall through to 'main'.
    if (videoItem.closest('#playlist-list')) {
        return 'playlist';
    }

    return 'main';
};

/**
 * Returns the video_click position (1-based) of an item from whichever
 * registered tracker knows it, or 0 when no tracker does — e.g. pages that
 * render a video list but instantiate no click tracker, or items appended
 * after init(); callers fall back to their own basis on 0.
 */
export const getTrackedVideoItemPosition = (videoItem: HTMLElement): number => {
    for (const tracker of activeTrackers) {
        const position = tracker._getVideoItemPosition(videoItem);

        if (position > 0) {
            return position;
        }
    }

    return 0;
};
